import { PassedSundaysResponse, PassedSundaysWrittenDownMark } from '../../../common/src/api_types.ts';
import { DateString, isValidDateString, maxDate, minDate } from '../../../common/src/date_string.ts';
import { Org, OrgId, OrgType } from '../../../common/src/model.ts';
import { hasRepeatingRow } from '../../../common/src/repeating_rows.ts';
import * as server_api from '../../../common/src/server_api.ts';
import {
  localStorageGet,
  LocalStorageKey,
  localStorageRemove,
  localStorageSet,
  PendingPassedSundaysWrittenDown,
} from './client_local_storage.ts';
import { classifySongListSyncFailure } from './song_list_sync_retry.ts';
import { useLocalStorage } from './use_local_storage.tsx';

// How far the server has written a church's passed Sundays down (issue #4192), from GET /api/passed-sundays, fetched
// with the church's song lists. Every reader passes it in its FillClock, so a passed Sunday the server has not written
// yet goes on showing its fill however long the server is behind, and the planner writes it down itself before a row
// change (getWriteDownBeforeRowChange). A date is only ever read with the lists it was fetched alongside: one fetched
// later could say a Sunday is written that those lists do not hold written yet, and the Sunday would show unfilled.
// So the Church tab's weekly sync passes its date straight to getWeeklySongLists, and the planner reads only the date
// stored with its own lists by the all-lists download (LocalStorageKey.AllSongListsWrittenThrough). Without a date
// (never heard from the server, or a failed fetch) a reader falls back to the clock (getFillStartSunday). Only a church
// with a repeating row asks (asksForWrittenThrough): any other org, a church without one included, fills nothing, so
// the clock it falls back to changes nothing for it.
//
// The planner writes passed Sundays down itself too, before a change (issue #4209, writePassedSundaysDown). Its stored
// date then moves to the last Sunday it wrote down: the lists it is stored with, the planner's cache, now hold that
// write-down, so the date still describes its own lists, and the planner reads those Sundays as stored at once, like
// any other past Sunday. The server is told, with the first Sunday written down too, by the mark
// (getPassedSundaysWrittenDownToSend), and from then on answers that date to every reader and leaves those Sundays
// alone in its own write-down. It ignores a write-down that starts after its first unwritten Sunday (a planner on the
// clock fallback writes down only the Sunday before the current one), which would skip the Sundays between, and then
// writes them down itself.
//
// The rule: the server gets a mark only together with, or after, every list of the write-downs it covers. A mark
// ahead of its lists would be dangerous: the server would leave those Sundays to lists that may never arrive (refused,
// discarded, or never queued because `write` threw), and every reader would read them unfilled meanwhile. A tab keeps
// it with a gate: writePassedSundaysDown stores the mark only once `write` has queued every list, and a sync pass
// first waits for every write-down of the tab in progress (passedSundaysWriteDownsSettled), then reads its queue
// timestamp and the mark with no await between, so a mark it reads was stored after all its lists were queued below
// that timestamp: they are in the pass, or an earlier pass uploaded them. The opposite slip is accepted: lists that reach the server ahead of their mark (a pass that had
// passed the gate before the write-down began) leave those Sundays unwritten on the server until the mark follows in
// the next pass, so only the server's automatic write-down (Monday from 10:00 UTC) landing in that gap could write a
// Sunday's fill into a cell the admin had emptied. Without a mark at all (`write` threw after queueing part of its
// lists, a refused mark, a clear of the pending work while `write` ran) the server writes the Sundays down itself, as
// it did before the planner did. Two planner tabs of one browser are not covered: they share the one queue and the
// one mark but each has its own gate and timestamp, and their queue keys can already collide (as before issue #4209);
// the app assumes one planner tab.
//
// The mark is kept in localStorage (LocalStorageKey.PassedSundaysWrittenDownPending), beside the IndexedDB queue of
// song list updates rather than in it: a stale bundle in another tab shares that queue and uploads every entry in it
// as a song list, so a mark queued there would reach the server as a list.

type PassedSundaysOrg = Pick<Org, 'id' | 'type' | 'churchLiturgySchema'>;

// whether the org has a date to ask for: the one question behind both the fetch and the stored date's readers
function asksForWrittenThrough(org: PassedSundaysOrg | undefined): org is PassedSundaysOrg {
  return hasRepeatingRow(org);
}

// The date the planner's lists of the org downloaded with; undefined when they downloaded without one, the stored
// lists are another org's, or the org does not ask for one (asksForWrittenThrough)
export function getWrittenThroughForOrg(org: PassedSundaysOrg): DateString | undefined {
  return writtenThroughFor(localStorageGet(LocalStorageKey.AllSongListsWrittenThrough), org);
}

// getWrittenThroughForOrg as a hook that follows the stored value
export function useWrittenThroughForOrg(org: PassedSundaysOrg | undefined): DateString | undefined {
  const [stored] = useLocalStorage(LocalStorageKey.AllSongListsWrittenThrough);
  return writtenThroughFor(stored, org);
}

function writtenThroughFor(
  stored: { orgId: OrgId; writtenThrough?: DateString } | undefined,
  org: PassedSundaysOrg | undefined,
): DateString | undefined {
  return asksForWrittenThrough(org) && stored?.orgId === org.id ? stored.writtenThrough : undefined;
}

/**
 * The server's answer for a church with a repeating row (asksForWrittenThrough), fetched before its song lists: an
 * answer read before the lists is never ahead of them (the server writes a Sunday's lists before it records the Sunday
 * as written), so a Sunday the downloaded lists do not hold written yet still shows its fill. Undefined, with no
 * request, for any other org, and when the request fails (offline, or an old server answering 404 during a deploy),
 * and the next sync asks again. Never throws.
 */
export async function fetchPassedSundays(org: PassedSundaysOrg): Promise<PassedSundaysResponse | undefined> {
  if (!asksForWrittenThrough(org)) {
    return undefined;
  }
  try {
    return await server_api.getPassedSundays(org.id);
  } catch (e: unknown) {
    console.warn(`Failed to fetch the passed Sundays written down: ${e instanceof Error ? e.message : String(e)}`);
    return undefined;
  }
}

// Stores what fetchPassedSundays answered for the org with the planner's lists just downloaded with it, in the same
// step as LocalStorageKey.AllSongLists. A failed fetch, or an answer with no date (the server has not run the
// write-down yet), stores none, so the planner falls back to the clock rather than pairing an earlier date with these
// lists.
export function storeAllSongListsWrittenThrough(orgId: OrgId, response: PassedSundaysResponse | undefined): void {
  const writtenThrough = response?.writtenThrough;
  localStorageSet(LocalStorageKey.AllSongListsWrittenThrough, {
    orgId,
    ...(isValidDateString(writtenThrough) && { writtenThrough }),
  });
}

// The write-downs of this tab in progress (writePassedSundaysDown), each settling once its `write` has ended and its
// mark, if any, is stored: a sync pass waits for them before it reads the mark (passedSundaysWriteDownsSettled)
const writeDownsInProgress = new Set<Promise<void>>();
// how many times the pending song list work was thrown away (clearPassedSundaysWrittenDownPending): a write-down whose
// `write` saw it move stores no mark, since the lists it covers may be gone with the queue
let pendingWorkClears = 0;

/**
 * Writes the church's passed Sundays down on the device, `writtenDown` being the first and last Sundays `write` writes
 * down (getUnwrittenPassedSundays), so that what the planner then holds for them stands (issue #4209): `write` queues
 * the write-down's lists, and any edit that goes with it. Only for a church that asks for the date
 * (asksForWrittenThrough, a church with a repeating row): any other org, a school, a small group or a church without a
 * repeating row, fills nothing, so there is nothing for the server to write down after the planner, and `write` simply
 * runs.
 * - While it runs, the write-down holds back every sync pass of the tab (passedSundaysWriteDownsSettled), the ones
 *   `write` starts itself included: `write` must start the song-list sync and never wait for it (useAllSongLists'
 *   updateEach starts it and goes on, even for no list), and the pass it starts uploads once the mark is stored, with
 *   the mark inside its lists' requests, or alone when the write-down queued no list.
 * - Once `write` has returned, every list it wrote is queued, and the Sundays are stored for the server
 *   (LocalStorageKey.PassedSundaysWrittenDownPending), merged with any the org has pending already: the earlier
 *   `from` and the later `writtenThrough` (each write-down starts where the one before left off), under a new token, so
 *   a pass that sent the earlier value does not clear this one.
 * - Then the planner's stored date moves to `writtenThrough` (the later of the two): its cached lists now hold the
 *   write-down, so it reads those Sundays as stored, with no fill. A date stored for another org, or none, is
 *   replaced: the lists `write` changed are this org's.
 * - If `write` throws, nothing is stored, the passes go on, and the throw goes on: whatever lists it queued go up
 *   without a mark, and the server writes those Sundays down itself.
 * - If the pending song list work is thrown away while `write` runs (a discard, a 403, a switch of org or household:
 *   clearPassedSundaysWrittenDownPending), nothing is stored either: some of the lists the mark would cover are gone,
 *   and a mark without them would have the server leave those Sundays unwritten.
 */
export async function writePassedSundaysDown(
  org: PassedSundaysOrg,
  writtenDown: PassedSundaysWrittenDownMark,
  write: () => Promise<void>,
): Promise<void> {
  if (!asksForWrittenThrough(org)) {
    await write();
    return;
  }
  const { promise: settled, resolve: settle } = Promise.withResolvers<void>();
  writeDownsInProgress.add(settled);
  try {
    const clearsBefore = pendingWorkClears;
    await write();
    if (pendingWorkClears !== clearsBefore) {
      return;
    }
    const { writtenThrough } = writtenDown;
    const pending = getPending();
    const orgPending = pending?.orgId === org.id ? pending : undefined;
    localStorageSet(LocalStorageKey.PassedSundaysWrittenDownPending, {
      orgId: org.id,
      from: orgPending ? minDate([orgPending.from, writtenDown.from]) : writtenDown.from,
      writtenThrough: laterOf(orgPending?.writtenThrough, writtenThrough),
      token: newToken(),
    });
    const stored = localStorageGet(LocalStorageKey.AllSongListsWrittenThrough);
    localStorageSet(LocalStorageKey.AllSongListsWrittenThrough, {
      orgId: org.id,
      writtenThrough: laterOf(stored?.orgId === org.id ? stored.writtenThrough : undefined, writtenThrough),
    });
  } finally {
    writeDownsInProgress.delete(settled);
    settle();
  }
}

/**
 * Resolves once no write-down of this tab is in progress (writePassedSundaysDown), one that began while waiting
 * included. A sync pass awaits it and then, with no await between, reads its queue timestamp and the pending mark
 * (uploadUpdatedSongLists), so the mark it reads was stored after every list of its write-downs was queued below that
 * timestamp. Never rejects.
 */
export async function passedSundaysWriteDownsSettled(): Promise<void> {
  while (writeDownsInProgress.size > 0) {
    await Promise.all(writeDownsInProgress);
  }
}

function laterOf(date: DateString | undefined, other: DateString): DateString {
  return date === undefined ? other : maxDate([date, other]);
}

function newToken(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// The pending write-down: an object with a valid orgId, from, writtenThrough and token, whatever else it holds.
// Anything else reads as none and is left as it is, never removed: a newer bundle in another tab may have stored a
// shape this one does not know. It then holds no sync back, and a write-down of this tab replaces it.
function getPending(): PendingPassedSundaysWrittenDown | undefined {
  const stored: unknown = localStorageGet(LocalStorageKey.PassedSundaysWrittenDownPending);
  if (typeof stored !== 'object' || stored === null || Array.isArray(stored)) {
    return undefined;
  }
  const { orgId, from, writtenThrough, token } = stored as Partial<
    Record<keyof PendingPassedSundaysWrittenDown, unknown>
  >;
  if (
    typeof orgId !== 'number' ||
    typeof from !== 'string' ||
    typeof writtenThrough !== 'string' ||
    typeof token !== 'string' ||
    !isValidDateString(from) ||
    !isValidDateString(writtenThrough)
  ) {
    return undefined;
  }
  return { orgId, from, writtenThrough, token };
}

// whether a Sunday the planner wrote down waits to be sent (writePassedSundaysDown): a sync pass has work to do
export function isPassedSundaysWrittenDownPending(): boolean {
  return getPending() !== undefined;
}

// The pending Sundays go with the pending song list updates they were written with, when those are thrown away, and
// so does the mark of a write-down whose `write` is running now (writePassedSundaysDown)
export function clearPassedSundaysWrittenDownPending(): void {
  pendingWorkClears++;
  removePending();
}

function removePending(): void {
  localStorageRemove(LocalStorageKey.PassedSundaysWrittenDownPending);
}

export interface PassedSundaysWrittenDownToSend {
  // the Sundays to send; undefined when none is pending for the org
  mark?: PassedSundaysWrittenDownMark;
  // the server has the mark (or refused it for good): clear it, unless a later write-down stored another meanwhile
  sent(): void;
}

/**
 * The pending write-down of `org`, the org whose lists this upload sends, for a sync pass that has waited for every
 * write-down in progress (passedSundaysWriteDownsSettled) and read its queue timestamp with no await since: every list
 * of the write-downs the mark covers is then queued below that timestamp, in this pass, or an earlier pass uploaded it.
 * The pass sends it inside its DELETE and its POST of /api/song-lists, which the server stores only for a batch it
 * accepts, and alone (sendPassedSundaysWrittenDown) only when it has no list request. A write-down pending for another
 * org, or for an org that is not a church, is dropped at once: its lists are not this upload's.
 */
export function getPassedSundaysWrittenDownToSend(org: Pick<Org, 'id' | 'type'>): PassedSundaysWrittenDownToSend {
  const pending = getPending();
  if (!pending) {
    return { sent: () => {} };
  }
  if (pending.orgId !== org.id || org.type !== OrgType.Church) {
    removePending();
    return { sent: () => {} };
  }
  return {
    mark: { from: pending.from, writtenThrough: pending.writtenThrough },
    sent: () => {
      const current = getPending();
      if (current?.orgId === pending.orgId && current.token === pending.token) {
        removePending();
      }
    },
  };
}
/**
 * Tells the server, with POST /api/passed-sundays, which passed Sundays the planner has written `org` down through
 * itself, for a sync pass with no list request to carry the mark (a write-down whose lists ended as they were: Clear
 * taking off the only fill it added, say). Throws, keeping the Sundays, on a failure that may pass (offline, 5xx, 401,
 * 408, 429), which fails the upload, and the pass retries. A refusal (403 staff or not an admin, 422 not an active
 * church, not a passed Sunday, or a `from` after the church's first Sunday not yet written down, an old server's 404
 * during a deploy, any other 4xx) drops them: nothing makes the same request acceptable later, and the server then
 * writes the Sundays down itself as before.
 */
export async function sendPassedSundaysWrittenDown(
  org: Pick<Org, 'id'>,
  { mark, sent }: PassedSundaysWrittenDownToSend,
): Promise<void> {
  if (!mark) {
    return;
  }
  try {
    await server_api.recordPassedSundaysWrittenDown(org.id, mark);
  } catch (e: unknown) {
    if (classifySongListSyncFailure(e).kind === 'transient') {
      throw e;
    }
    console.warn(
      `The server refused the passed Sundays written down from ${mark.from} through ${mark.writtenThrough}: ${e instanceof Error ? e.message : String(e)}`,
    );
  }
  sent();
}
